import {
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  Injectable,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TuiHintHover } from '@taiga-ui/core';
import { fromEvent } from 'rxjs';

const AUTO_CLOSE_DELAY = 2500;

const isCoarsePointer =
  typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches;

interface OpenHintEntry {
  readonly host: HTMLElement;
  close(): void;
}

/**
 * Общий на всё приложение реестр открытых хинтов и единая точка для
 * document/window слушателей. Компоненты с таблицами/списками могут навешивать
 * [tuiHint] на десятки элементов одновременно — если каждый экземпляр
 * директивы сам поставит touchstart/scroll listener на document/window,
 * получится O(N) обработчиков на каждое событие скролла. Вместо этого здесь
 * хранятся только реально открытые в данный момент хинты (обычно 0-1
 * во всём приложении), и глобальные слушатели работают с этим маленьким
 * набором независимо от того, сколько [tuiHint] смонтировано на странице.
 */
@Injectable({ providedIn: 'root' })
class MobileHintDismissRegistry {
  private readonly openHints = new Set<OpenHintEntry>();
  private listenersBound = false;

  markOpen(entry: OpenHintEntry): void {
    this.openHints.add(entry);
    this.bindGlobalListenersOnce();
  }

  markClosed(entry: OpenHintEntry): void {
    this.openHints.delete(entry);
  }

  private bindGlobalListenersOnce(): void {
    if (this.listenersBound) {
      return;
    }

    this.listenersBound = true;

    document.addEventListener(
      'touchstart',
      event => {
        const target = event.target as Node;

        this.openHints.forEach(entry => {
          if (!entry.host.contains(target)) {
            entry.close();
          }
        });
      },
      { capture: true },
    );

    window.addEventListener(
      'scroll',
      () => this.openHints.forEach(entry => entry.close()),
      { capture: true, passive: true },
    );
  }
}

/**
 * Универсальный фикс для любого [tuiHint] в приложении, не привязан к
 * конкретной фиче — подключайте в любом компоненте, где хинты показываются
 * на тач-устройствах.
 *
 * tuiHint полагается на mouseenter/mouseleave/mouseout (TuiHoveredService).
 * На тач-устройствах браузер эмулирует mouseenter при тапе, но mouseleave
 * почти никогда не срабатывает — хинт «залипает» и перекрывает контент.
 * Директива добивает закрытие: по тапу вне контрола, по скроллу и по таймауту.
 * На устройствах с мышью (курсор не coarse) ничего не делает — обычное
 * hover-поведение хинта не меняется.
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[tuiHint]:not(ng-container):not(ng-template)',
  standalone: true,
})
export class DismissMobileHintDirective {
  private readonly host = inject(ElementRef<HTMLElement>).nativeElement;
  private readonly hover = inject(TuiHintHover, { self: true });
  private readonly registry = inject(MobileHintDismissRegistry);
  private readonly destroyRef = inject(DestroyRef);
  private readonly entry: OpenHintEntry = {
    host: this.host,
    close: () => this.close(),
  };
  private closeTimeout?: ReturnType<typeof setTimeout>;

  constructor() {
    if (!isCoarsePointer) {
      return;
    }

    fromEvent(this.host, 'mouseenter')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.open());

    this.destroyRef.onDestroy(() => this.close());
  }

  private open(): void {
    this.registry.markOpen(this.entry);
    clearTimeout(this.closeTimeout);
    this.closeTimeout = setTimeout(() => this.close(), AUTO_CLOSE_DELAY);
  }

  private close(): void {
    clearTimeout(this.closeTimeout);
    this.registry.markClosed(this.entry);
    this.hover.toggle(false);
  }
}
